import { useEffect, useState } from "react";
import type { ClockReading } from "@infoflyt/shared/timecode";
import { readClock, recalibrate } from "./clock";

/**
 * Tikkfrekvensen for tidskodeavlesningen.
 *
 * Frame-tallet endrer seg 25 ganger i sekundet, men det er dempet ned nettopp
 * fordi ingen leser det. 100 ms gir sekundene riktig og holder opptegningene
 * nede — skjermen står på store deler av et skift.
 */
const TICK_MS = 100;

/**
 * Holder avlesningen i gang mens appen er synlig, og stopper den når den
 * ikke er det. Ingen polling i bakgrunnen: ankeret ligger i lokal lagring,
 * så tidskoden regnes fram igjen ved oppvåkning uten at noe går tapt.
 */
/** Det `recalibrate` fant. `deltaMs` er avviket mellom to kalibreringer. */
export interface Fremhenting {
  readonly changed: boolean;
  readonly deltaMs: number;
}

export function useClockTick(
  active: boolean,
  onResume?: (m: Fremhenting) => void,
): ClockReading {
  const [reading, setReading] = useState<ClockReading>(readClock);

  useEffect(() => {
    if (!active) return;
    let timer: number | null = window.setInterval(() => setReading(readClock()), TICK_MS);

    const stop = (): void => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const start = (): void => {
      if (timer === null) timer = window.setInterval(() => setReading(readClock()), TICK_MS);
    };

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        // Appen kan ha vært borte lenge nok til at tellingen er nullstilt.
        void recalibrate().then((r) => {
          setReading(readClock());
          /*
           * Kalles ALLTID, ikke bare naar forskyvningen ble satt paa nytt.
           * `deltaMs` er maalingen drifttesten lever av, og den ble foer
           * kastet her — ogsaa i de tilfellene der det var noe aa maale.
           * Se docs/10-slagplan.md.
           */
          onResume?.(r);
        });
        start();
      } else {
        stop();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, onResume]);

  return reading;
}

/**
 * Holder skjermen våken mens det logges.
 *
 * Dette er ikke pynt: en installert iOS-webapp kan avlives når skjermen slås
 * av, og da nullstilles den monotone tellingen. Låsen er derfor en del av
 * tidskodens holdbarhet, ikke en bekvemmelighet. Virker i installerte
 * webapper fra iOS 18.4.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !("wakeLock" in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const acquire = async (): Promise<void> => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
      } catch {
        // Nektet eller ikke tilgjengelig. Appen virker uansett.
      }
    };

    const onVisibility = (): void => {
      // Låsen slippes automatisk når siden skjules og må tas på nytt.
      if (document.visibilityState === "visible" && !released) void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release();
    };
  }, [enabled]);
}
